import React, { useEffect, useState, forwardRef } from 'react';
import Questions from '../../lib/Questions';
import SidePanel from '../SidePanel.jsx';
import EditQuestionForm from './EditQuestionForm';
import { TrashIcon } from '@heroicons/react/24/outline';

/**
 * Edit question panel for invoices - works with local state instead of API
 */
const InvoiceEditQuestionPanel = forwardRef(
    ({ question, setQuestion, onUpdate, onDelete }, ref) => {
        const [showEditPanel, setShowEditPanel] = useState(false);
        const [label, setLabel] = useState(question?.label);
        const [type, setType] = useState(question?.type);
        const [settings, setSettings] = useState(question?.settings);

        // Track original values for cancel
        const [origLabel, setOrigLabel] = useState(question?.label);
        const [origType, setOrigType] = useState(question?.type);
        const [origSettings, setOrigSettings] = useState(question?.settings);

        const questionSchema = Questions[question?.type] || {};

        useEffect(() => {
            if (question) {
                setLabel(question.label);
                setType(question.type);
                setSettings(question.settings);
                // Store original values
                setOrigLabel(question.label);
                setOrigType(question.type);
                setOrigSettings(question.settings);
            }
        }, [question?.id]);

        useEffect(() => {
            if (!question) {
                setShowEditPanel(false);
            } else {
                setShowEditPanel(true);
            }
        }, [question?.id]);

        useEffect(() => {
            if (!showEditPanel) {
                setQuestion(null);
            }
        }, [showEditPanel]);

        // Update parent state whenever local state changes
        useEffect(() => {
            if (showEditPanel && question) {
                onUpdate(question, { label, type, settings });
            }
        }, [label, type, JSON.stringify(settings)]);

        function handleDelete() {
            onDelete(question);
            setShowEditPanel(false);
        }

        function handleClose() {
            setShowEditPanel(false);
        }

        function handleCancel() {
            // Revert to original values
            if (question) {
                setLabel(origLabel);
                setType(origType);
                setSettings(origSettings);
                // Update parent state to revert changes
                onUpdate(question, { label: origLabel, type: origType, settings: origSettings });
            }
            setShowEditPanel(false);
        }

        if (!question) {
            return null;
        }

        return (
            <SidePanel
                isOpen={showEditPanel}
                onClose={handleClose}
                onCancel={handleCancel}
                title={`Edit ${questionSchema.name || 'Question'}`}
                showSave={true}
                onSave={handleClose}
                saveLabel="Done"
            >
                <EditQuestionForm
                    label={label}
                    setLabel={setLabel}
                    settings={settings}
                    setSettings={setSettings}
                    type={type}
                    formDetails={{}}
                    question={question}
                    url=""
                />

                {!questionSchema.cannotDelete && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="btn-pink-pill flex items-center gap-2"
                        >
                            <TrashIcon className="w-4 h-4" />
                            Delete
                        </button>
                    </div>
                )}
            </SidePanel>
        );
    },
);

export default InvoiceEditQuestionPanel;

